import React, { useState, useMemo } from "react";
import { Container } from "reactstrap";
import { Link } from "react-router-dom";
import { HugeiconsIcon } from "@hugeicons/react";
import {
  DeleteIcon,
} from "@hugeicons/core-free-icons";
import { useGetArrivalsQuery } from "../../services/arrivals";
import { ImportCampaignModal } from "../Campaigns/ImportCampaignModal";
import { useTranslation } from "react-i18next";
import BtnAsLink from "../../Component/BtnAsLink";
import ConfirmModal from "../../CommonElements/ConfirmModal";
import { Trans } from "react-i18next";
import { TableC } from "../../CommonElements/TableC";
import { useLocalizedText } from "../../utils/hooks/useLocalizedText";
import { useDebouncedSearch } from "../../utils/hooks/useDebouncedSearch";
import { visitDateOptions } from "../Campaigns/data";
import { SelectCountry } from "../../Component/Form/SelectCountry/SelectCountry";
import { useDeleteInitialCampaignMutation } from "@/services/campaigns";
import { PieChartIcon, Xsl02Icon } from "@hugeicons/core-free-icons/index";
import { useImportDepartureMutation } from "@/services/departures";
import { LabelValue } from "@/Component/Value";
import { useSystem } from "@/utils/hooks/useLocation";
import { MAKKAH_PERMISSIONS } from "@/utils/constants";
import { Can } from "@/CommonElements/PermissionGate";
import { generatePermissionName } from "@/utils/permissions";
import usePermissions from "@/utils/hooks/usePermissions";


// Extract column configuration
;

export const Departure = () => {
  const { t } = useTranslation();
  const { hasAnyPermission } = usePermissions();
  const [showImportModal, setShowImportModal] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const { port, from_id } = useSystem();
  const [filters, setFilters] = useState({
    direction_id: from_id,
  });
  const [deleteModalState, setDeleteModalState] = useState({
    isOpen: false,
    arrivalId: null,
    entry_port: null,
  });
  const importMutation = useImportDepartureMutation();


  const { getLocalizedText } = useLocalizedText();

  const { searchValue, debouncedValue, setSearchValue } = useDebouncedSearch(
    "",
    500
  );

  const {
    data: arrivalsRes,
    isLoading,
  } = useGetArrivalsQuery({
    search: debouncedValue,
    page: currentPage,
    per_page: 10,
    ...filters,
  });

  const arrivals = arrivalsRes?.data?.data || [];
  const meta = arrivalsRes?.data?.meta;
  const loading = isLoading;

  // Delete mutation
  const { mutate: deleteArrival, isPending: isDeleting } =
    useDeleteInitialCampaignMutation();

  const handleSearchChange = (e) => {
    setSearchValue(e.target.value);
    setCurrentPage(1);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleFilterApply = (filterData) => {
    setFilters(filterData);
    setCurrentPage(1);
  };

  const handleFilterReset = () => {
    setFilters({
      direction_id: from_id,
    });
    setCurrentPage(1);
  };

  const handleDeleteCancel = () => {
    setDeleteModalState({ isOpen: false, arrivalId: null, entry_port: null });
  };

  const handleDeleteConfirm = () => {
    if (deleteModalState.arrivalId) {
      deleteArrival(
        {
          id: deleteModalState.arrivalId,
          entry_port: deleteModalState.entry_port,
        },
        {
          onSuccess: () => {
            setDeleteModalState({
              isOpen: false,
              arrivalId: null,
              entry_port: null,
            });
          },
          onError: () => {
            setDeleteModalState({
              isOpen: false,
              arrivalId: null,
              entry_port: null,
            });
          },
        }
      );
    }
  };




  // Define columns configuration with render functions
  const columns = useMemo(
    () =>
      [
        { name: t("UPCOMING_COL_SERIAL"), key: "campaign_number", filterable: true },
        { name: t("UPCOMING_COL_MANAGER"), key: "campaign_name", filterable: true },
        {
          name: t("UPCOMING_COL_NATIONALITY"),
          key: "nationality_id",
          filterable: true,
          render: (value) => <SelectCountry.Display value={value} />,
        },
        { 
          name: t("UPCOMING_COL_COUNTRY"), 
          key: "country", 
          filterable: true, 
          render: (value) => <LabelValue.Value value={getLocalizedText(value?.name_ar, value?.name_en)} /> 
        },
        { 
          name: t("UPCOMING_COL_CITY"), 
          key: "state", 
          filterable: true, 
          render: (value) => <LabelValue.Value value={getLocalizedText(value?.name_ar, value?.name_en)} /> },
        {
          name: t("UPCOMING_COL_PILGRIMS"),
          key: "total_capacity",
          filterable: true,
        },
        {
          name: t("MAKKAH_SERVICE_CENTER_ID"),
          key: "service_center",
          render: (value) => <LabelValue.Value value={getLocalizedText(value?.name_ar, value?.name_en)} />,

        },
        {
          name: t("MADINA_SERVICE_CENTER_ID"),
          key: "madina_service_center",
          render: (value) => <LabelValue.Value value={getLocalizedText(value?.name_ar, value?.name_en)} />,
        },
        { name: t("UPCOMING_COL_VISIT"), 
          key: "visit", 
          filterable: true,
          render: (value) => t(value?.toUpperCase())
        },
        {
          name: t("UPCOMING_COL_FLIGHTS_COUNT"),
          key: "departure_count",
          filterable: true,
        },
        {
          name: t("UPCOMING_COL_PORT"),
          key: "entry_port",
          filterable: true,
          visible: false,
        },
        {
          name: t("UPCOMING_COL_FIRST_FLIGHT"),
          key: "first_flight_date",
          filterable: true,
          visible: false,
        },
        {
          name: t("UPCOMING_COL_LAST_FLIGHT"),
          key: "last_flight_date",
          filterable: true,
          visible: false,
        },
        {
          name: t("UPCOMING_COL_MOBILE"),
          key: "mobile",
          filterable: true,
          visible: false,
        },
        hasAnyPermission([
          generatePermissionName(from_id, MAKKAH_PERMISSIONS.view_campaign_details),
          generatePermissionName(from_id, MAKKAH_PERMISSIONS.create_campaign_details),
          generatePermissionName(from_id, MAKKAH_PERMISSIONS.delete_campaign_details),
        ]) && { name: t("UPCOMING_COL_ACTIONS"), key: "actions", filterable: false },
      ].filter(Boolean),
    [t, hasAnyPermission, from_id, getLocalizedText]
  );

  // Define actions function
  const getActions = (rowData) => {
    const editUrl = `${rowData.id}/${port}/edit`;

    return [
      {
        label: (
          <Link to={editUrl} className="d-flex align-items-center gap-2 text-nowrap">
            <HugeiconsIcon size={18} icon={PieChartIcon} />
            <span>{t("MANAGE_SEGMENTS")}</span>
          </Link>
        ),
        key: "add-details",
      },
    ];
  };

  // Add button dropdown
  const addButton = (
    <Can permissions={[
      { systemId: from_id, permissionName: MAKKAH_PERMISSIONS.import_campaigns },
    ]}>
      <BtnAsLink
        disabled={loading}
        onClick={() => setShowImportModal(true)}
        className="btn-primary"
        icon={<HugeiconsIcon icon={Xsl02Icon} />}
      >
        {t("IMPORT_DETAILS")}
      </BtnAsLink>
    </Can>
  );


  const filterFields = [
    {
      name: "date",
      label: t("DATE"),
      type: "date",
    },
    {
      name: "campaign_number",
      label: t("CAMPAIGN_NUMBER"),
      type: "input",
    },
    {
      name: "nationality_id",
      label: t("UPCOMING_COL_NATIONALITY"),
      type: "select",
      optionKey: "nationalities",
      options: (n) => ({
        value: n.id,
        label: <SelectCountry.Display value={n.id} />,
      }),
    },
    {
      name: "country_id",
      label: t("UPCOMING_COL_COUNTRY"),
      type: "select",
      optionKey: "nationalities",
      options: (c) => ({
        value: c.id,
        label: <SelectCountry.Display value={c.id} />,
      }),
    },
    {
      name: "state_id",
      label: t("UPCOMING_COL_CITY"),
      type: "select",
      optionKey: "states",
      options: (s) => ({
        value: s.id,
        label: [s.code, s.name].join(" - "),
      }),
    },
    {
      name: "service_center_id",
      label: t("UPCOMING_COL_MAKTAB"),
      type: "select",
      optionKey: "service_centers",
      options: (sc) => ({
        value: sc.id,
        label: sc.name,
      }),
    },
    {
      name: "makkah_contract_id",
      label: t("MAKKAH_CONTRACTS"),
      type: "select",
      optionKey: "contracts",
      filter: (c) => c.city_id === "603",
      options: (c) => ({
        value: c.id,
        label: [c.id, c.house_commercial_name_ar].join(" - "),
      }),
    },
    {
      name: "madinah_contract_id",
      label: t("MADINAH_CONTRACTS"),
      type: "select",
      optionKey: "contracts",
      filter: (c) => c.city_id === "605",
      options: (c) => ({
        value: c.id,
        label: [c.id, c.house_commercial_name_ar].join(" - "),
      }),
    },
    {
      name: "visit",
      label: t("UPCOMING_COL_VISIT"),
      type: "select",
      options: visitDateOptions(t),
    },
  ];

  return (
    <Container fluid>
      <TableC
        data={arrivals}
        columns={columns}
        loading={loading}
        searchValue={searchValue}
        onSearchChange={handleSearchChange}
        searchPlaceholder={t("SEARCH_HERE")}
        pagination={
          meta
            ? {
              current: currentPage,
              total: meta.total,
              pageSize: meta.per_page,
              onChange: handlePageChange,
            }
            : undefined
        }
        actions={getActions}
        renderActions={addButton}
        // filterModal={CustomFilterModal}
        filterFields={filterFields}
        filters={filters}
        onFilterApply={handleFilterApply}
        onFilterReset={handleFilterReset}
        emptyStateTitle={
          debouncedValue ? t("NO_SEARCH_RESULTS") : t("NO_UPCOMMING_DEPARTURE")
        }
        emptyStateDescription={
          debouncedValue
            ? t("NO_SEARCH_RESULTS_DESCRIPTION")
            : t("NO_UPCOMMING_DEPARTURE_DESCRIPTION")
        }
        minVisibleColumns={2}
        skeletonCount={3}

        headerKey="campaign_number"
        headerRender={(data) => <LabelValue label={t("CAMPAIGN_NUMBER") + ": "} value={data.campaign_number} />}
      />

      {/* Delete Confirmation Modal */}
      <ConfirmModal
        isOpen={deleteModalState.isOpen}
        onToggle={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        title={t("CONFIRM")}
        message={
          <Trans
            i18nKey="CONFIRM_DELETE_ARRIVAL"
            components={{
              danger: <span className="text-danger" />,
            }}
          />
        }
        dangerText={t("DELETE")}
        confirmText={t("DELETE")}
        cancelText={t("CANCEL")}
        isLoading={isDeleting}
        defaultLoadingText={t("DELETING")}
        loadingText={t("DELETING")}
        confirmVariant="danger"
        confirmIcon={DeleteIcon}
      />

      {/* Import Campaign Modal */}
      <ImportCampaignModal
        isOpen={showImportModal}
        toggle={() => setShowImportModal(false)}
        modalTitle={t("IMPORT_DEPARTURES")}
        importMutation={importMutation}
      />
    </Container>
  );
};

export default Departure;
